import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import authService from "../services/authService";

const PasswordResetRequest = () => {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({ identifier: "" });
  const currentYear = new Date().getFullYear();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const { response, data } = await authService.requestPasswordReset(
        formData.identifier
      );

      if (response.ok) {
        Swal.fire("Password reset email sent successfully!", "", "success");
        setFormData({ identifier: "" });
        setError("");
      } else {
        setError(data.error || "User not found. Please try again.");
      }
    } catch {
      setError("Failed to send password reset request. Please try again.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F7FAFC] text-[#0F172A] font-[Manrope]">
      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-5">
          {/* Left: Logo + Name + Tagline */}
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img
              src="/deebug-logo-light.png"
              alt="DEEBug logo"
              className="h-16 w-auto"
            />
            <div className="flex flex-col leading-tight">
              <span className="text-2xl font-extrabold text-[#0EA5E9] tracking-wide">
                DEEBug
              </span>
              <span className="text-sm text-[#334155] font-medium">
                <strong>D</strong>etect.<strong>E</strong>xplain.
                <strong>E</strong>nhance.<strong>B</strong>ug
              </span>
            </div>
          </div>

          {/* Center Nav - Desktop */}
          <nav className="hidden md:flex items-center gap-8 text-lg font-semibold text-[#334155]">
            <Link to="/resources" className="hover:text-[#0EA5E9] transition">
              Resources
            </Link>
          </nav>

          {/* Right: Auth Buttons + Mobile Menu Toggle */}
          <div className="flex items-center gap-2">
            {/* Auth buttons (hidden on very small screens, shown from sm+) */}
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={() => navigate("/login")}
                className="text-[#0F172A] font-semibold text-base hover:text-[#0284C7] transition"
              >
                Sign in
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="border border-[#0EA5E9] text-[#0EA5E9] text-base px-4 py-2 rounded-md font-semibold hover:bg-[#E0F2FE] transition"
              >
                Sign up
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              className="inline-flex items-center justify-center md:hidden p-2 rounded-md border border-[#E2E8F0] hover:bg-[#F1F5F9] focus:outline-none"
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              aria-label="Toggle navigation menu"
            >
              <svg
                className="h-6 w-6 text-[#0F172A]"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeWidth="1.8"
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeWidth="1.8"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-[#E2E8F0] bg-white">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-4">
              <nav className="flex flex-col gap-2 text-base font-semibold text-[#334155]">
                <Link to="/resources" className="hover:text-[#0EA5E9] transition">
                  Resources
                </Link>
              </nav>

              <div className="flex flex-col gap-2 pt-2 border-t border-[#E2E8F0]">
                <button
                  onClick={() => {
                    navigate("/login");
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left text-[#0F172A] font-semibold text-base hover:text-[#0284C7] transition"
                >
                  Sign in
                </button>
                <button
                  onClick={() => {
                    navigate("/signup");
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full border border-[#0EA5E9] text-[#0EA5E9] text-base px-4 py-2 rounded-md font-semibold hover:bg-[#E0F2FE] transition"
                >
                  Sign up
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* MAIN */}
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-10">
          <h2 className="text-3xl font-extrabold text-[#0F172A] text-center mb-8">
            Reset your password
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="identifier"
                className="block text-base font-medium text-[#0F172A] mb-2"
              >
                Email address
              </label>
              <input
                type="email"
                id="identifier"
                name="identifier"
                value={formData.identifier}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-[#E2E8F0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0EA5E9]/80 transition"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-[#0EA5E9] text-white py-2.5 rounded-md font-semibold hover:bg-[#0284C7] transition"
            >
              Send Reset Email
            </button>

            {error && (
              <p className="text-[#EF4444] text-base text-center mt-3">{error}</p>
            )}
          </form>

          <div className="mt-8 text-center">
            <p className="text-[#334155]">
              Remembered your password?{" "}
              <button
                onClick={() => navigate("/login")}
                className="text-[#0EA5E9] font-medium hover:underline"
              >
                Log In
              </button>
            </p>
          </div>
        </div>
      </main>

      {/* FOOTER (UPDATED) */}
      <footer className="bg-[#F1F5F9] border-t border-[#E2E8F0] py-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between px-6 gap-4 text-[#475569] text-base">
          <p className="font-medium">
            © {currentYear} DEE­Bug™. All rights reserved.
          </p>

          <div className="flex flex-wrap justify-center gap-8">
            <Link to="/privacy" className="hover:text-[#0EA5E9] transition font-medium">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-[#0EA5E9] transition font-medium">
              Terms
            </Link>
            <Link to="/security" className="hover:text-[#0EA5E9] transition font-medium">
              Security
            </Link>
            <Link to="/contact" className="hover:text-[#0EA5E9] transition font-medium">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PasswordResetRequest;
